/** @jsxImportSource @emotion/react */
import React, { ReactNode } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Breakpoint } from '@mui/material/styles';
import { breakpoints } from '@zip/business-components';
import { theme } from 'global';

interface HiddenProps {
  children: ReactNode;
  xsDown?: boolean;
  xsUp?: boolean;
  smDown?: boolean;
  smUp?: boolean;
  mdDown?: boolean;
  mdUp?: boolean;
  lgDown?: boolean;
  lgUp?: boolean;
  xlDown?: boolean;
  xlUp?: boolean;
  only?: Breakpoint | Breakpoint[];
  className?: string;
  css?: any;
}

/**
 * Hidden component - A replacement for the deprecated MUI Hidden component
 * Uses CSS display:none to hide elements (preserves DOM structure like MUI's original)
 */
const Hidden: React.FC<HiddenProps> = ({
  children,
  xsDown,
  xsUp,
  smDown,
  smUp,
  mdDown,
  mdUp,
  lgDown,
  lgUp,
  xlDown,
  xlUp,
  className,
  css,
}) => {
  // ----- directional props -----
  // Hooks must be called unconditionally, so we evaluate all media queries
  const matchesXsDown = useMediaQuery(breakpoints.down(theme.breaks.xs));
  const matchesXsUp = useMediaQuery(breakpoints.up(theme.breaks.xs));
  const matchesSmDown = useMediaQuery(breakpoints.down(theme.breaks.sm));
  const matchesSmUp = useMediaQuery(breakpoints.up(theme.breaks.sm));
  const matchesMdDown = useMediaQuery(breakpoints.down(theme.breaks.md));
  const matchesMdUp = useMediaQuery(breakpoints.up(theme.breaks.md));
  const matchesLgDown = useMediaQuery(breakpoints.down(theme.breaks.lg));
  const matchesLgUp = useMediaQuery(breakpoints.up(theme.breaks.lg));
  const matchesXlDown = useMediaQuery(breakpoints.down(theme.breaks.xl));
  const matchesXlUp = useMediaQuery(breakpoints.up(theme.breaks.xl));

  const hide =
    (xsDown && matchesXsDown) ||
    (xsUp && matchesXsUp) ||
    (smDown && matchesSmDown) ||
    (smUp && matchesSmUp) ||
    (mdDown && matchesMdDown) ||
    (mdUp && matchesMdUp) ||
    (lgDown && matchesLgDown) ||
    (lgUp && matchesLgUp) ||
    (xlDown && matchesXlDown) ||
    (xlUp && matchesXlUp);

  // Use CSS display:none instead of returning null
  // This preserves DOM structure like MUI's original Hidden component
  // display: 'contents' makes the wrapper transparent in the layout when visible
  return (
    <div
      className={className}
      css={css}
      style={{ display: hide ? 'none' : 'contents' }}
    >
      {children}
    </div>
  );
};

export default Hidden;
